import { Injectable, inject } from '@angular/core';
import {
  ContactList,
  ContactListConnection,
} from '../../model/contacts/contact-list';
import { DirtyStatus } from '../../model/common/dirty-status';
import { Contact } from '../../model/contacts/contact';
import { ContactListMember } from '../../model/contacts/contact-list-member';
import { FacilityBuilderService } from './facility-builder.service';
import { onlyUnique } from '../../core/functions/common-functions';
import { ContactBuilderService } from './contact-builder.service';
import {
  ExternalContactList,
  ExternalContactListConnection,
} from '../../model/contacts/external-contact-list';
import { ContactListUpdater } from '../../model/contacts/contact-list-updater';
import { ContactListSearch } from '../../model/contacts/contact-list-search-results';
import { Schedule } from '../../model/contacts/contact-list-schedule';
import { utc } from 'moment';
import { getCorrectedDate } from '../../utils/utils';
import { ConfigContactListItem } from '../../model/common/config-item';
import { AdditionalResourceListType } from '../../model/contacts/additional-resource-list-type';
import { AdditionalResourceList } from '../../model/contacts/additional-resource-list';
import { MemberCode } from 'src/app/model/contacts/member-code';

@Injectable({
  providedIn: 'root',
})
export class ContactListBuilderService {
  private _facilityBuilder = inject(FacilityBuilderService);
  private _contactBuilder = inject(ContactBuilderService);

  buildContactListQuery(filters: any) {
    const query = {
      state: null,
      county: null,
      township: null,
      name: null,
      code: null,
      businessUnits: null,
      additionalResourceLists: false,
    };

    if (filters.state) {
      query.state = filters.state.value;
    }
    if (filters.county) {
      query.county = filters.county.value;
    }
    if (filters.township) {
      query.township = filters.township.value;
    }
    if (filters.name) {
      query.name = filters.name.value;
    }
    if (filters.code) {
      query.code = filters.code.value;
    }
    if (filters.businessUnits) {
      query.businessUnits = filters.businessUnits;
    }

    if (filters.additionalResourceLists) {
      query.additionalResourceLists = filters.additionalResourceLists;
    }

    return query;
  }

  buildExternalContactListQuery(filters: any) {
    const query = {
      name: null,
      businessUnits: null,
    };

    if (filters.name) {
      query.name = filters.name.value;
    }
    if (filters.businessUnits) {
      query.businessUnits = filters.businessUnits;
    }

    return query;
  }

  buildContactListSearch(item: any): ContactListSearch {
    return {
      id: item.id,
      name: item.name,
      code: item.memberCodes,
      state: item.state,
      county: item.county,
      township: item.township,
    };
  }

  buildContactList(item: any): ContactList | null {
    if (item === null) {
      return null;
    }

    const retVal: ContactList = {
      id: item.id,
      code: item.MemberCodes
        ? item.MemberCodes.map((mc: any) => mc.code)
            .filter(onlyUnique)
            .join(', ')
        : '',
      memberCodes: item.MemberCodes
        ? item.MemberCodes.map((mc: any) => ({
            id: mc.id,
            code: mc.code,
          }))
        : [],
      origMemberCodes: item.MemberCodes ? item.MemberCodes : [],
      name: item.name,
      members: item.ContactListMembers
        ? item.ContactListMembers.map((clm: any) =>
            this.buildContactListMember(clm)
          )
        : [],
      updaters: item.ContactListUpdaters
        ? item.ContactListUpdaters.map((clm: any) => ({
            id: clm.id,
            contactId: clm.ContactId,
            contactListId: item.id,
          }))
        : [],
      facilities: item.Facilities
        ? item.Facilities.map((f: any) => {
            return this._facilityBuilder.buildFacility(f, []);
          })
        : [],
      ManagementFacilities: item.ManagementFacilities
        ? item.ManagementFacilities.map((f: any) => {
            return this._facilityBuilder.buildFacility(f, []);
          })
        : [],
      AdditionalResourceListFacilities: item.AdditionalResourceListFacilities
        ? item.AdditionalResourceListFacilities.map((f: any) => {
            return this._facilityBuilder.buildFacility(f, []);
          })
        : [],
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      deletedAt: item.deletedAt,
      businessUnits: item.BusinessUnits,
      AdditionalResourceListType: item.AdditionalResourceListType
        ? item.AdditionalResourceListType
        : null,
    };
    retVal['state'] = retVal.facilities
      ?.map((f: any) => f.state)
      .filter(onlyUnique)
      .join(', ');
    retVal['county'] = retVal.facilities
      ?.map((f) => f.county)
      .filter(onlyUnique)
      .join(', ');
    retVal['township'] = retVal.facilities
      ?.map((f) => f.locations.map((l) => l.township))
      .reduce((acc, val) => acc.concat(val), []) // .flat isn't supported in IE/EDGE
      .filter(onlyUnique)
      .join(', ');
    return retVal;
  }

  buildContactListForDropdown(item: any): ConfigContactListItem {
    return {
      id: item.id,
      name: item.name,
      additionalResourceListTypeId: item.additionalResourceListTypeId,
    };
  }

  buildContactListMember(item: any): ContactListMember {
    return {
      id: item.id,
      contactListId: item.ContactListId,
      contactId: item.ContactId,
      order: item.order,
      contact: item.Contact
        ? this._contactBuilder.buildContact(item.Contact)
        : null,
      isScheduled: item.isScheduled ?? false,
    };
  }

  buildNullExternalContactList(): ExternalContactList {
    return {
      id: null,
      name: null,
      notes: null,
      deletedAt: null,
      members: [],
      businessUnits: [],
    } as unknown as ExternalContactList;
  }

  buildExternalContactList(item: any): ExternalContactList {
    const retVal: ExternalContactList = {
      id: item.id,
      name: item.name,
      notes: item.notes ? item.notes : null,
      deletedAt: item.deletedAt ? new Date(item.deletedAt) : undefined,
      updatedAt: item.updatedAt ? new Date(item.updatedAt) : undefined,
      updater: item.updater ? item.updater : null,
      members: item.ExternalContactListMembers
        ? item.ExternalContactListMembers.map((clm: any) => ({
            id: clm.id,
            name: clm.name,
            description: clm.description,
            phone: clm.phone,
            order: clm.order,
          }))
        : [],
      businessUnits: item.BusinessUnits
        ? item.BusinessUnits.map((bu: any) => ({
            id: bu.id,
            name: bu.name,
          }))
        : [],
    };

    return retVal;
  }

  buildNewMember(
    contactListId: string,
    contact: Contact,
    order: number
  ): ContactListMember {
    return {
      id: '',
      contactListId: contactListId,
      contactId: contact.id,
      contact: contact,
      order: order,
      dirtyStatus: DirtyStatus.NEW,
      isScheduled: false,
    };
  }

  buildNewUpdater(contactListId: string, data: any): ContactListUpdater {
    return {
      id: '',
      contactListId: contactListId,
      contactId: data.value,
      label: data.label,
      dirtyStatus: DirtyStatus.NEW,
    };
  }

  buildUpdateStatement(contactList: ContactList, changes: any) {
    const retVal: any = {
      id: contactList.id,
      memberCodes: changes.memberCodes
        ? changes.memberCodes.map((mc: any) => ({
            id: mc.id,
            code: mc.code,
          }))
        : [],
      notes: changes.notes ? changes.notes : null,
      name: changes.name ? changes.name : contactList.name,
      members: contactList.members
        ? contactList.members.map((m) => ({
            id: m.id,
            ContactListId: contactList.id,
            ContactId: m.contactId,
            order: m.order,
          }))
        : null,
      updaters: contactList.updaters
        ? contactList.updaters.map((m) => ({
            id: m.id,
            ContactListId: contactList.id,
            ContactId: m.contactId,
          }))
        : null,
      businessUnits: changes.businessUnits.map((bu: any) => {
        return {
          id: bu,
        };
      }),
      additionalResourceListTypeId: changes?.additionalResourceListTypeId
        ? changes.additionalResourceListTypeId
        : null,
    };

    if (changes.increment) {
      retVal['increment'] = changes.increment;
    }

    return retVal;
  }

  buildExternalContactListUpdateStatement(
    contactList: ExternalContactList,
    changes: any
  ) {
    const retVal: any = {
      id: contactList.id ? contactList.id : '',
      members: contactList.members
        ? contactList.members.map((m) => ({
            id: m.id ? m.id : '',
            ExternalContactListId: contactList.id,
            order: m.order,
            name: m.name,
            description: m.description,
            phone: m.phone,
            dirtyStatus: m.dirtyStatus ? m.dirtyStatus : 0,
          }))
        : null,
      businessUnits: contactList.businessUnits,
    };

    if (changes.name) {
      retVal['name'] = changes.name;
    }
    if (changes.businessUnits) {
      retVal['businessUnits'] = changes.businessUnits.map((bu: string) => ({
        id: bu,
      }));
    }
    retVal['notes'] = changes.notes ? changes.notes : contactList.notes;
    return retVal;
  }

  buildContactListSchedule(s: Schedule): Schedule {
    let cloned = { ...s };
    cloned.utcStartTime = utc(cloned.startTime).format('YYYY-MM-DDTHH:mm:ss');
    cloned.utcEndTime = utc(cloned.endTime).format('YYYY-MM-DDTHH:mm:ss');

    cloned.startTime = getCorrectedDate(cloned.utcStartTime)!;
    cloned.endTime = getCorrectedDate(cloned.utcEndTime)!;
    return cloned;
  }

  buildAdditionalResourceListType(
    resourceListType: any
  ): AdditionalResourceListType {
    const retVal = {
      id: resourceListType.id,
      name: resourceListType.name,
      enabled: resourceListType.enabled,
      deletedAt: resourceListType.deletedAt,
    };

    return retVal;
  }

  buildAdditionalResourceList(
    resourceList: any
  ): AdditionalResourceList | null {
    if (resourceList === null) {
      return null;
    }

    const retVal = {
      id: resourceList.id,
      name: resourceList.name,
      ContactListMembers: resourceList.ContactListMembers
        ? resourceList.ContactListMembers.map((clm: any) =>
            this.buildContactListMember(clm)
          )
        : [],
      AdditionalResourceListType: resourceList.AdditionalResourceListType
        ? resourceList.AdditionalResourceListType
        : null,
    };

    return retVal;
  }

  buildContactListConnection(item: any): ContactListConnection {
    return {
      items: item.items.map((cls: any) => this.buildContactListSearch(cls)),
      totalRecords: item.totalRecords,
    };
  }

  buildMemberCode(item: any): MemberCode {
    return { id: item.id, code: item.code };
  }

  buildExternalContactListConnection(i: any): ExternalContactListConnection {
    return {
      items: i.items.map((item: any) => this.buildExternalContactList(item)),
      totalRecords: i.totalRecords,
    };
  }
}
